// src/components/KidzCorner.jsx

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { db } from '../config/firebase-config';
import { collection, onSnapshot, doc, getDoc, setDoc } from 'firebase/firestore';
import {
    BookOpen, Gamepad2, Star, ArrowLeft, Trophy,
    Download, Play, Sun, RefreshCw, Zap, Headphones,
    FileText, X, ExternalLink,
    CheckCircle2, Smile, Ear, Flower2, HeartHandshake
} from 'lucide-react';
import { useOrganization } from '../context/OrganizationContext';
import { PiggyBank } from 'lucide-react';
import FinanceHubView from './finance/FinanceHubView';
import { isFinanceHubEnabled } from '../data/financeHub';
import SupportOutlet from './dashboard/SupportOutlet';
import { hasSupportOutlet } from '../utils/resolveSupportContact';

// --- ADVANCED YOUTUBE PLAYER ---
const CustomYouTubePlayer = ({ videoId, onNext }) => {
    const containerRef = useRef(null);
    const playerRef = useRef(null);
    const [isEnded, setIsEnded] = useState(false);

    useEffect(() => {
        setIsEnded(false); 
        if (!videoId) return;

        const loadAPI = () => {
            return new Promise((resolve) => {
                if (window.YT && window.YT.Player) {
                    resolve(window.YT);
                    return;
                }
                
                if (!document.querySelector('script[src="https://www.youtube.com/iframe_api"]')) {
                    const tag = document.createElement('script');
                    tag.src = 'https://www.youtube.com/iframe_api';
                    const firstScriptTag = document.getElementsByTagName('script')[0];
                    firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
                }
                
                window.onYouTubeIframeAPIReady = () => resolve(window.YT);
            });
        };

        let player;
        loadAPI().then((YT) => {
            if (!containerRef.current) return;
            
            const el = document.createElement('div');
            el.className = "w-full h-full absolute inset-0";
            containerRef.current.innerHTML = '';
            containerRef.current.appendChild(el);

            player = new YT.Player(el, {
                videoId: videoId,
                playerVars: { 
                    rel: 0, 
                    modestbranding: 1, 
                    playsinline: 1,
                    showinfo: 0
                },
                events: {
                    onStateChange: (event) => {
                        if (event.data === YT.PlayerState.ENDED) {
                            
                            // --- NEW: FORCE EXIT FULLSCREEN ON END ---
                            if (document.fullscreenElement) {
                                document.exitFullscreen().catch(() => {});
                            } else if (document.webkitFullscreenElement) { /* Safari */
                                document.webkitExitFullscreen().catch(() => {});
                            } else if (document.mozFullScreenElement) { /* Firefox */
                                document.mozCancelFullScreen().catch(() => {});
                            } else if (document.msFullscreenElement) { /* IE11 */
                                document.msExitFullscreen().catch(() => {});
                            }

                            setIsEnded(true);
                        } else if (event.data === YT.PlayerState.PLAYING) {
                            setIsEnded(false);
                        }
                    }
                }
            });
            playerRef.current = player;
        });

        return () => {
            if (playerRef.current && playerRef.current.destroy) {
                playerRef.current.destroy();
            }
        };
    }, [videoId]);

    return (
        <div className="relative w-full h-full bg-black rounded-xl overflow-hidden shadow-2xl ring-1 ring-white/10">
            {/* YouTube Iframe mounts here */}
            <div ref={containerRef} className="absolute inset-0 w-full h-full"></div>
            
            {/* The ERI HUB Custom End Screen Overlay */}
            {isEnded && (
                <div className="absolute inset-0 z-50 bg-slate-900 flex flex-col items-center justify-center animate-in fade-in duration-300">
                    <div className="w-16 h-16 md:w-20 md:h-20 bg-blue-500/10 rounded-full flex items-center justify-center mb-6 border border-blue-500/20 shadow-[0_0_30px_rgba(59,130,246,0.2)]">
                        <CheckCircle2 size={40} className="text-blue-500" />
                    </div>
                    <h3 className="text-white text-2xl md:text-3xl font-black mb-8 text-center px-4 tracking-tight">Video Complete</h3>
                    
                    <div className="flex flex-col sm:flex-row gap-4">
                        <button 
                            onClick={() => {
                                setIsEnded(false);
                                if (playerRef.current) playerRef.current.playVideo();
                            }} 
                            className="px-6 py-3.5 bg-white/10 hover:bg-white/20 text-white rounded-xl font-bold transition-colors flex items-center justify-center gap-2 border border-white/10"
                        >
                            <RefreshCw size={18} /> Replay
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

// ==========================================
// GAME 1: EMOTION MEMORY MATCH
// ==========================================
const MEMORY_EMOJIS = ['😀', '😢', '😡', '😱', '😍', '😴', '😎', '🤔'];

const MemoryGame = ({ onComplete }) => {
    const [cards, setCards] = useState([]);
    const [flipped, setFlipped] = useState([]);
    const [solved, setSolved] = useState([]);
    const [moves, setMoves] = useState(0);
    const [won, setWon] = useState(false);

    const shuffleCards = useCallback(() => {
        const deck = [...MEMORY_EMOJIS, ...MEMORY_EMOJIS]
            .sort(() => Math.random() - 0.5)
            .map((emoji, index) => ({ id: index, content: emoji }));
        setCards(deck);
        setFlipped([]);
        setSolved([]);
        setMoves(0);
        setWon(false);
    }, []);

    useEffect(() => { shuffleCards(); }, [shuffleCards]);

    const handleCardClick = (id) => {
        if (flipped.length === 2 || flipped.includes(id) || solved.includes(id)) return;
        setFlipped([...flipped, id]);
    };

    useEffect(() => {
        if (flipped.length === 2) {
            setMoves(m => m + 1);
            const [first, second] = flipped;
            if (cards[first].content === cards[second].content) {
                setSolved([...solved, first, second]);
                setFlipped([]);
            } else {
                setTimeout(() => setFlipped([]), 1000);
            }
        }
    }, [flipped, cards, solved]);

    useEffect(() => {
        if (cards.length > 0 && solved.length === cards.length && !won) {
            setWon(true);
            onComplete(50); 
        }
    }, [solved, cards, onComplete, won]);

    return (
        <div className="flex flex-col items-center w-full max-w-lg mx-auto animate-in fade-in">
            <div className="flex justify-between w-full mb-4 font-bold text-slate-600">
                <span>Moves: {moves}</span>
                <button onClick={shuffleCards} className="flex items-center gap-1 text-blue-500 hover:underline">
                    <RefreshCw size={16}/> Restart
                </button>
            </div>
            <div className="grid grid-cols-4 gap-3 w-full aspect-square">
                {cards.map((card) => (
                    <button
                        key={card.id}
                        onClick={() => handleCardClick(card.id)}
                        className={`rounded-xl text-3xl md:text-5xl flex items-center justify-center transition-all duration-500 transform ${
                            flipped.includes(card.id) || solved.includes(card.id) 
                                ? 'bg-white rotate-0 shadow-md border-2 border-blue-200' 
                                : 'bg-gradient-to-br from-blue-400 to-blue-600 rotate-180 text-transparent'
                        }`}
                        disabled={won}
                    >
                        {(flipped.includes(card.id) || solved.includes(card.id)) ? card.content : ''}
                    </button>
                ))}
            </div>
            {won && (
                <div className="mt-6 p-4 bg-green-100 text-green-700 rounded-xl font-bold flex items-center gap-2 animate-in slide-in-from-bottom">
                    <Trophy size={24} className="text-yellow-500" />
                    You matched all emotions! +50 Stars!
                </div>
            )}
        </div>
    );
};

// ==========================================
// GAME 2: BREATHING BUBBLE
// ==========================================
const BreathingGame = ({ onComplete }) => {
    const [phase, setPhase] = useState('Ready');
    const [scale, setScale] = useState(1);
    const [active, setActive] = useState(false);

    useEffect(() => {
        if (!active) return;
        const cycle = () => {
            setPhase('Inhale...'); setScale(1.5);
            setTimeout(() => {
                setPhase('Hold');
                setTimeout(() => {
                    setPhase('Exhale...'); setScale(1);
                    setTimeout(() => { setPhase('Hold'); }, 4000);
                }, 2000);
            }, 4000);
        };
        cycle();
        const interval = setInterval(cycle, 12000);
        const rewardTimer = setTimeout(() => {
            onComplete(20); setActive(false); setPhase("Great Job!");
        }, 60000); 
        return () => { clearInterval(interval); clearTimeout(rewardTimer); };
    }, [active, onComplete]);

    return (
        <div className="flex flex-col items-center justify-center h-96 relative overflow-hidden bg-sky-50 rounded-3xl border border-sky-100 animate-in fade-in">
            {!active ? (
                <button 
                    onClick={() => setActive(true)}
                    className="z-10 px-8 py-4 bg-blue-500 text-white rounded-full font-black text-xl shadow-xl hover:scale-105 transition-transform flex items-center gap-2"
                >
                    <Play fill="currentColor" /> START BREATHING
                </button>
            ) : (
                <>
                    <div 
                        className="w-48 h-48 bg-gradient-to-br from-blue-400 to-indigo-400 rounded-full flex items-center justify-center shadow-lg transition-all duration-[4000ms] ease-in-out relative z-10"
                        style={{ transform: `scale(${scale})` }}
                    >
                        <span className="text-white font-bold text-lg drop-shadow-md">{phase}</span>
                    </div>
                    <div className="absolute bottom-6 text-slate-400 text-sm font-medium animate-pulse">
                        Complete 1 minute for +20 Stars
                    </div>
                </>
            )}
        </div>
    );
};

// ==========================================
// GAME 3: AFFIRMATION BALLOON POP
// ==========================================
const AFFIRMATIONS = ["I am brave!", "I can do hard things!", "My voice matters!", "I am loved!", "It's okay to cry!", "I am a leader!", "I keep trying!", "I am enough!"];
const BALLOON_COLORS = ["bg-red-400", "bg-blue-400", "bg-green-400", "bg-yellow-400", "bg-purple-400", "bg-pink-400"];

const BalloonGame = ({ onComplete }) => {
    const [balloons, setBalloons] = useState([]);
    const [score, setScore] = useState(0);
    const [timeLeft, setTimeLeft] = useState(30);
    const [active, setActive] = useState(false);
    const [message, setMessage] = useState("");


    useEffect(() => {
        if (!active) return;
        const timer = setInterval(() => {
            setTimeLeft(t => {
                if (t <= 1) {
                    setActive(false); onComplete(Math.min(score, 50)); return 0;
                } return t - 1;
            });
        }, 1000);
        const spawner = setInterval(() => {
            const id = Date.now();
            const color = BALLOON_COLORS[Math.floor(Math.random() * BALLOON_COLORS.length)];
            const left = Math.floor(Math.random() * 80) + 10;
            const text = AFFIRMATIONS[Math.floor(Math.random() * AFFIRMATIONS.length)];
            setBalloons(prev => [...prev, { id, color, left, text, popped: false }]);
        }, 800);
        return () => { clearInterval(timer); clearInterval(spawner); };
    }, [active, onComplete, score]); 

    const popBalloon = (id, text) => {
        setMessage(text); setScore(s => s + 5);
        setBalloons(prev => prev.map(b => b.id === id ? { ...b, popped: true } : b));
        setTimeout(() => {
            setBalloons(prev => prev.filter(b => b.id !== id)); setMessage("");
        }, 1000);
    };

    return (
        <div className="relative h-[500px] w-full bg-sky-100 rounded-3xl overflow-hidden border-2 border-sky-200 shadow-inner animate-in fade-in">
            <div className="absolute top-4 left-4 right-4 flex justify-between z-20 font-bold text-slate-700 bg-white/50 p-2 rounded-xl backdrop-blur-sm">
                <span>Score: {score}</span>
                <span className={`${timeLeft < 10 ? 'text-red-500 animate-pulse' : ''}`}>Time: {timeLeft}s</span>
            </div>
            {message && (
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-30 pointer-events-none">
                    <span className="text-3xl font-black text-white drop-shadow-lg animate-in zoom-in duration-200 text-center whitespace-nowrap bg-black/20 px-4 py-2 rounded-xl backdrop-blur-md">{message}</span>
                </div>
            )}
            {!active && timeLeft === 30 && (
                <div className="absolute inset-0 z-40 flex items-center justify-center bg-white/60 backdrop-blur-sm">
                    <button onClick={() => setActive(true)} className="px-8 py-4 bg-pink-500 text-white rounded-full font-black text-xl shadow-xl hover:scale-105 transition-transform flex items-center gap-2"><Play fill="currentColor"/> START POPPING</button>
                </div>
            )}
            {!active && timeLeft === 0 && (
                <div className="absolute inset-0 z-40 flex flex-col items-center justify-center bg-white/80 backdrop-blur-sm">
                    <h3 className="text-3xl font-black text-slate-800 mb-2">Time's Up!</h3>
                    <p className="text-lg text-slate-600 mb-6">You popped {score/5} balloons!</p>
                    <button onClick={() => { setTimeLeft(30); setScore(0); setBalloons([]); }} className="px-6 py-3 bg-blue-500 text-white rounded-xl font-bold hover:bg-blue-600">Play Again</button>
                </div>
            )}
            <div className="absolute inset-0 overflow-hidden">
                {balloons.map(b => (
                    <button key={b.id} onClick={() => !b.popped && popBalloon(b.id, b.text)} disabled={b.popped} className={`absolute bottom-0 w-16 h-20 rounded-full cursor-pointer transition-transform active:scale-95 flex items-center justify-center ${b.color} ${b.popped ? 'scale-150 opacity-0 transition-all duration-300' : 'animate-float-up'}`} style={{ left: `${b.left}%`, animationDuration: '4s', animationTimingFunction: 'linear', animationFillMode: 'forwards' }}>
                        {!b.popped && <div className="absolute -bottom-4 w-0.5 h-4 bg-slate-400 opacity-50"></div>}
                        {!b.popped && <div className="absolute top-3 right-3 w-3 h-3 bg-white/40 rounded-full"></div>}
                    </button>
                ))}
            </div>
            <style>{` @keyframes floatUp { from { transform: translateY(500px); } to { transform: translateY(-600px); } } .animate-float-up { animation-name: floatUp; } `}</style>
        </div>
    );
};

// ==========================================
// GAME 4: SIMON SAYS (FOCUS PATTERN)
// ==========================================
const SimonGame = ({ onComplete }) => {
    const colors = ['green', 'red', 'yellow', 'blue'];
    const [sequence, setSequence] = useState([]);
    const [playing, setPlaying] = useState(false);
    const [playingIdx, setPlayingIdx] = useState(0);
    const [userTurn, setUserTurn] = useState(false);
    const [score, setScore] = useState(0);
    const [gameOver, setGameOver] = useState(false);
    const [activeColor, setActiveColor] = useState(null);

    const startGame = () => {
        setSequence([]);
        setScore(0);
        setGameOver(false);
        setPlaying(true);
        addNewColor([]);
    };

    const addNewColor = (currentSeq) => {
        const color = colors[Math.floor(Math.random() * 4)];
        const newSeq = [...currentSeq, color];
        setSequence(newSeq);
        setPlayingIdx(0);
        setUserTurn(false);
        playSequence(newSeq);
    };

    const playSequence = (seq) => {
        let i = 0;
        const interval = setInterval(() => {
            setActiveColor(seq[i]);
            setTimeout(() => setActiveColor(null), 400); 
            i++;
            if (i >= seq.length) {
                clearInterval(interval);
                setUserTurn(true);
            }
        }, 800); 
    };

    const handleColorClick = (color) => {
        if (!playing || !userTurn || gameOver) return;
        setActiveColor(color);
        setTimeout(() => setActiveColor(null), 200);

        if (color !== sequence[playingIdx]) {
            setGameOver(true);
            setPlaying(false);
            if (score > 5) onComplete(score * 2); 
        } else {
            if (playingIdx + 1 === sequence.length) {
                setScore(s => s + 1);
                setTimeout(() => addNewColor(sequence), 1000);
            } else {
                setPlayingIdx(i => i + 1);
            }
        }
    };

    return (
        <div className="flex flex-col items-center justify-center w-full max-w-md mx-auto animate-in fade-in">
            <div className="mb-6 text-center">
                {!playing && !gameOver && (
                    <button onClick={startGame} className="px-8 py-3 bg-slate-900 text-white rounded-xl font-bold text-lg hover:bg-slate-800 shadow-lg flex items-center gap-2 mx-auto">
                        <Zap size={20} className="fill-yellow-400 text-yellow-400"/> Start Focus
                    </button>
                )}
                {(playing || gameOver) && <div className="text-2xl font-black text-slate-800">Score: {score}</div>}
                {gameOver && <div className="text-red-500 font-bold mt-2">Game Over! Try again.</div>}
                {userTurn && playing && <div className="text-blue-500 font-bold mt-2 animate-pulse">Your Turn!</div>}
            </div>

            <div className="grid grid-cols-2 gap-4 w-full aspect-square max-w-[300px]">
                {colors.map(color => (
                    <button
                        key={color}
                        onClick={() => handleColorClick(color)}
                        className={`
                            rounded-2xl transition-all duration-100 border-4 border-slate-100
                            ${color === 'green' ? 'bg-green-500' : ''}
                            ${color === 'red' ? 'bg-red-500' : ''}
                            ${color === 'yellow' ? 'bg-yellow-400' : ''}
                            ${color === 'blue' ? 'bg-blue-500' : ''}
                            ${activeColor === color ? 'brightness-125 scale-105 shadow-[0_0_20px_rgba(0,0,0,0.2)] z-10' : 'opacity-90'}
                        `}
                    />
                ))}
            </div>
        </div>
    );
};

// ==========================================
// GAME 5: FEELINGS DETECTIVE (EMOTIONAL LITERACY)
// ==========================================
const FeelingsDetectiveGame = ({ onComplete }) => {
    const FEELINGS = [
        { word: 'Happy', emoji: '😀' },
        { word: 'Sad', emoji: '😢' },
        { word: 'Worried', emoji: '😟' },
        { word: 'Calm', emoji: '😌' },
        { word: 'Excited', emoji: '🤩' },
        { word: 'Frustrated', emoji: '😤' },
        { word: 'Nervous', emoji: '😳' },
        { word: 'Proud', emoji: '🤗' },
    ];
    const TOTAL_ROUNDS = 5;

    const makeRound = () => {
        const shuffled = [...FEELINGS].sort(() => Math.random() - 0.5);
        const answer = shuffled[0];
        const options = [answer, shuffled[1], shuffled[2]].sort(() => Math.random() - 0.5);
        return { answer, options };
    };

    const [round, setRound] = useState(1);
    const [current, setCurrent] = useState(makeRound);
    const [feedback, setFeedback] = useState(null); // 'nice' | 'tryagain' | null
    const [locked, setLocked] = useState(false);
    const [done, setDone] = useState(false);

    const handlePick = (option) => {
        if (locked || done) return;
        if (option.word === current.answer.word) {
            setFeedback('nice');
            setLocked(true);
            setTimeout(() => {
                setFeedback(null);
                setLocked(false);
                if (round >= TOTAL_ROUNDS) {
                    setDone(true);
                    onComplete(25);
                } else {
                    setRound(r => r + 1);
                    setCurrent(makeRound());
                }
            }, 1100);
        } else {
            setFeedback('tryagain');
            setTimeout(() => setFeedback(f => (f === 'tryagain' ? null : f)), 1100);
        }
    };

    const restart = () => {
        setRound(1);
        setCurrent(makeRound());
        setFeedback(null);
        setLocked(false);
        setDone(false);
    };

    if (done) {
        return (
            <div className="flex flex-col items-center justify-center h-96 animate-in fade-in text-center">
                <div className="text-6xl mb-4">🕵️</div>
                <h3 className="text-2xl font-black text-slate-800 mb-2">Case closed, Detective!</h3>
                <p className="text-slate-500 font-medium mb-6">You matched all {TOTAL_ROUNDS} feelings. Knowing feelings is a superpower.</p>
                <div className="p-4 bg-green-100 text-green-700 rounded-xl font-bold flex items-center gap-2 mb-6">
                    <Trophy size={24} className="text-yellow-500" /> +25 Stars!
                </div>
                <button onClick={restart} className="flex items-center gap-1 text-blue-500 font-bold hover:underline">
                    <RefreshCw size={16}/> Play Again
                </button>
            </div>
        );
    }

    return (
        <div className="flex flex-col items-center w-full max-w-md mx-auto animate-in fade-in">
            <div className="flex justify-between w-full mb-6 font-bold text-slate-600">
                <span>Round {round} of {TOTAL_ROUNDS}</span>
                <span className="text-slate-400">🕵️ Feelings Detective</span>
            </div>
            <p className="text-slate-500 font-medium mb-2">Which face looks...</p>
            <div className="text-3xl md:text-4xl font-black text-slate-800 mb-8 px-8 py-4 bg-indigo-50 border border-indigo-100 rounded-2xl">
                {current.answer.word}?
            </div>
            <div className="grid grid-cols-3 gap-4 w-full">
                {current.options.map((option) => (
                    <button
                        key={option.word}
                        onClick={() => handlePick(option)}
                        disabled={locked}
                        className="aspect-square rounded-2xl bg-white border-b-4 border-slate-200 hover:border-indigo-400 hover:-translate-y-1 active:translate-y-0 transition-all text-5xl md:text-6xl flex items-center justify-center shadow-sm"
                    >
                        {option.emoji}
                    </button>
                ))}
            </div>
            <div className="h-14 mt-6 flex items-center justify-center">
                {feedback === 'nice' && (
                    <div className="px-5 py-2 bg-green-100 text-green-700 rounded-full font-bold animate-in zoom-in duration-200">
                        Nice! That's {current.answer.word.toLowerCase()} {current.answer.emoji}
                    </div>
                )}
                {feedback === 'tryagain' && (
                    <div className="px-5 py-2 bg-amber-100 text-amber-700 rounded-full font-bold animate-in zoom-in duration-200">
                        Almost — try again! You've got this 💛
                    </div>
                )}
            </div>
        </div>
    );
};

// ==========================================
// GAME 6: 5-4-3-2-1 GROUNDING (CALMING SKILL)
// ==========================================
const GroundingGame = ({ onComplete }) => {
    const STEPS = [
        { count: 5, emoji: '👀', title: 'things you can SEE', hint: 'Look around slowly. A window? A colour you like? Tap once for each thing you spot.' },
        { count: 4, emoji: '👂', title: 'things you can HEAR', hint: 'Get quiet and listen. Maybe a fan, a voice, or your own breathing.' },
        { count: 3, emoji: '✋', title: 'things you can TOUCH', hint: 'Feel your chair, your shirt, the floor under your feet.' },
        { count: 2, emoji: '👃', title: 'things you can SMELL', hint: 'Take a slow sniff. It can even be a smell you just remember.' },
        { count: 1, emoji: '💛', title: 'good thing about YOU', hint: 'Just one. Maybe you are kind, funny, brave, or a great friend.' },
    ];

    const [started, setStarted] = useState(false);
    const [stepIdx, setStepIdx] = useState(0);
    const [tapped, setTapped] = useState(0);
    const [done, setDone] = useState(false);
    const step = STEPS[stepIdx];

    const handleTap = () => {
        if (done) return;
        const next = tapped + 1;
        if (next < step.count) {
            setTapped(next);
        } else {
            // Gentle pause before the next sense.
            setTapped(next);
            setTimeout(() => {
                if (stepIdx + 1 >= STEPS.length) {
                    setDone(true);
                    onComplete(20);
                } else {
                    setStepIdx(i => i + 1);
                    setTapped(0);
                }
            }, 900);
        }
    };

    const restart = () => {
        setStarted(false); setStepIdx(0); setTapped(0); setDone(false);
    };

    return (
        <div className="flex flex-col items-center justify-center min-h-[420px] bg-sky-50 rounded-3xl border border-sky-100 p-8 animate-in fade-in text-center">
            {!started ? (
                <>
                    <div className="text-5xl mb-4">🧘</div>
                    <h3 className="text-2xl font-black text-slate-800 mb-2">5-4-3-2-1 Grounding</h3>
                    <p className="text-slate-500 font-medium max-w-sm mb-8">
                        When things feel like a lot, your senses can bring you back.
                        We'll go slowly, together. No rush at all.
                    </p>
                    <button
                        onClick={() => setStarted(true)}
                        className="px-8 py-4 bg-teal-500 text-white rounded-full font-black text-xl shadow-xl hover:scale-105 transition-transform flex items-center gap-2"
                    >
                        <Play fill="currentColor" /> LET'S BEGIN
                    </button>
                </>
            ) : done ? (
                <div className="animate-in fade-in duration-500">
                    <div className="text-5xl mb-4">🌈</div>
                    <h3 className="text-2xl font-black text-slate-800 mb-2">You brought yourself back to calm.</h3>
                    <p className="text-slate-500 font-medium max-w-sm mb-6 mx-auto">
                        That was real. You can use 5-4-3-2-1 anywhere, any time you need it.
                    </p>
                    <div className="p-4 bg-green-100 text-green-700 rounded-xl font-bold inline-flex items-center gap-2 mb-6">
                        <Trophy size={24} className="text-yellow-500" /> +20 Stars
                    </div>
                    <div>
                        <button onClick={restart} className="flex items-center gap-1 text-blue-500 font-bold hover:underline mx-auto">
                            <RefreshCw size={16}/> Do it again sometime
                        </button>
                    </div>
                </div>
            ) : (
                <div key={stepIdx} className="animate-in fade-in duration-500 w-full max-w-sm">
                    <div className="text-slate-400 font-bold text-sm mb-4 tracking-wider uppercase">Step {stepIdx + 1} of {STEPS.length}</div>
                    <div className="text-6xl mb-4">{step.emoji}</div>
                    <h3 className="text-2xl font-black text-slate-800 mb-2">
                        Find {step.count} {step.title}
                    </h3>
                    <p className="text-slate-500 font-medium mb-8">{step.hint}</p>
                    <div className="flex justify-center gap-3 mb-8">
                        {Array.from({ length: step.count }).map((_, i) => (
                            <div
                                key={i}
                                className={`w-4 h-4 rounded-full transition-all duration-500 ${
                                    i < tapped ? 'bg-teal-400 scale-110' : 'bg-slate-200'
                                }`}
                            />
                        ))}
                    </div>
                    <button
                        onClick={handleTap}
                        disabled={tapped >= step.count}
                        className="px-8 py-4 bg-white border-b-4 border-teal-200 text-teal-600 rounded-2xl font-black text-lg shadow-sm hover:-translate-y-0.5 active:translate-y-0 transition-all disabled:opacity-60"
                    >
                        {tapped >= step.count ? 'Beautiful... ✨' : step.count === 1 ? 'I thought of it 💛' : 'I found one'}
                    </button>
                </div>
            )}
        </div>
    );
};

// ==========================================
// GAME 7: GRATITUDE GARDEN (POSITIVE PSYCHOLOGY — PERSISTS)
// ==========================================
const GratitudeGarden = ({ onComplete, currentUser }) => {
    const PRESETS = ['A friend', 'Something I ate', 'Playing outside', 'My family', 'A pet or animal', 'Something that made me laugh'];
    const FLOWERS = ['🌷', '🌻', '🌱', '🌸'];
    const MAX_ITEMS = 3;
    const MAX_ENTRIES = 30;

    const [items, setItems] = useState([]);
    const [customText, setCustomText] = useState('');
    const [priorEntries, setPriorEntries] = useState([]);
    const [done, setDone] = useState(false);
    const [saving, setSaving] = useState(false);

    // Load prior garden entries (if signed in). No uid = works, just doesn't save.
    useEffect(() => {
        if (!currentUser?.uid) return;
        let cancelled = false;
        (async () => {
            try {
                const snap = await getDoc(doc(db, "users", currentUser.uid));
                const garden = snap.exists() && Array.isArray(snap.data().gratitudeGarden)
                    ? snap.data().gratitudeGarden
                    : [];
                if (!cancelled) setPriorEntries(garden);
            } catch (e) {
                console.error('Error loading gratitude garden:', e);
            }
        })();
        return () => { cancelled = true; };
    }, [currentUser?.uid]);

    const addItem = (text) => {
        const clean = (text || '').trim().slice(0, 80);
        if (!clean || done || items.length >= MAX_ITEMS) return;
        setItems(prev => (prev.length >= MAX_ITEMS ? prev : [...prev, clean]));
    };

    const removeItem = (idx) => {
        if (done) return;
        setItems(prev => prev.filter((_, i) => i !== idx));
    };

    const plantGarden = async () => {
        if (items.length < MAX_ITEMS || done || saving) return;
        setSaving(true);
        setDone(true);
        onComplete(25);
        // Persist to users/{uid}.gratitudeGarden — capped array, uid-guarded.
        if (currentUser?.uid) {
            try {
                const entry = { date: new Date().toISOString(), items };
                const updated = [...priorEntries, entry].slice(-MAX_ENTRIES);
                await setDoc(doc(db, "users", currentUser.uid), { gratitudeGarden: updated }, { merge: true });
                setPriorEntries(updated);
            } catch (e) {
                console.error('Error saving gratitude garden:', e);
            }
        }
        setSaving(false);
    };

    const flowerFor = (idx) => FLOWERS[idx % FLOWERS.length];

    return (
        <div className="flex flex-col items-center w-full max-w-lg mx-auto animate-in fade-in">
            <div className="text-center mb-6">
                <div className="text-5xl mb-3">🌼</div>
                <h3 className="text-2xl font-black text-slate-800 mb-1">Gratitude Garden</h3>
                <p className="text-slate-500 font-medium">
                    {done
                        ? 'Look at what you grew today!'
                        : `Plant ${MAX_ITEMS} good things about today and watch your garden grow.`}
                </p>
                {priorEntries.length > 0 && (
                    <p className="text-emerald-600 text-sm font-bold mt-2">
                        🌿 Your garden has grown {priorEntries.length} {priorEntries.length === 1 ? 'time' : 'times'}
                    </p>
                )}
            </div>

            {/* THE GARDEN ROW */}
            <div className="w-full bg-gradient-to-b from-sky-50 to-emerald-50 border border-emerald-100 rounded-3xl p-6 mb-6">
                <div className="flex justify-center gap-6 min-h-[72px] items-end">
                    {items.length === 0 && (
                        <span className="text-slate-300 font-bold self-center">Your garden is waiting... 🌱</span>
                    )}
                    {items.map((item, idx) => (
                        <button
                            key={idx}
                            onClick={() => removeItem(idx)}
                            disabled={done}
                            title={done ? item : `${item} (tap to remove)`}
                            className="flex flex-col items-center gap-1 group"
                        >
                            <span className="text-5xl animate-in zoom-in duration-300">{flowerFor(idx)}</span>
                            <span className="text-[10px] font-bold text-emerald-700 max-w-[90px] truncate">{item}</span>
                        </button>
                    ))}
                </div>
                <div className="mt-3 h-1.5 bg-emerald-200/60 rounded-full"></div>
            </div>

            {!done ? (
                <>
                    <div className="flex flex-wrap justify-center gap-2 mb-4">
                        {PRESETS.map(preset => (
                            <button
                                key={preset}
                                onClick={() => addItem(preset)}
                                disabled={items.length >= MAX_ITEMS}
                                className="px-4 py-2 bg-white border border-emerald-200 text-emerald-700 rounded-full font-bold text-sm hover:bg-emerald-50 transition-colors disabled:opacity-40"
                            >
                                {preset}
                            </button>
                        ))}
                    </div>
                    <div className="flex w-full max-w-sm gap-2 mb-6">
                        <input
                            type="text"
                            value={customText}
                            onChange={(e) => setCustomText(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') { addItem(customText); setCustomText(''); } }}
                            placeholder="Or type your own good thing..."
                            maxLength={80}
                            disabled={items.length >= MAX_ITEMS}
                            className="flex-1 px-4 py-2.5 bg-white border border-slate-200 rounded-xl font-medium text-slate-700 placeholder:text-slate-300 focus:outline-none focus:border-emerald-300 disabled:opacity-40"
                        />
                        <button
                            onClick={() => { addItem(customText); setCustomText(''); }}
                            disabled={!customText.trim() || items.length >= MAX_ITEMS}
                            className="px-4 py-2.5 bg-emerald-500 text-white rounded-xl font-bold hover:bg-emerald-600 transition-colors disabled:opacity-40"
                        >
                            Add
                        </button>
                    </div>
                    <button
                        onClick={plantGarden}
                        disabled={items.length < MAX_ITEMS}
                        className="px-8 py-4 bg-emerald-500 text-white rounded-full font-black text-lg shadow-xl hover:scale-105 transition-transform disabled:opacity-40 disabled:hover:scale-100"
                    >
                        🌷 Plant My Garden ({items.length}/{MAX_ITEMS})
                    </button>
                </>
            ) : (
                <div className="text-center animate-in slide-in-from-bottom">
                    <div className="p-4 bg-green-100 text-green-700 rounded-xl font-bold inline-flex items-center gap-2 mb-3">
                        <Trophy size={24} className="text-yellow-500" /> +25 Stars — your garden grew!
                    </div>
                    <p className="text-slate-500 font-medium max-w-sm mx-auto">
                        Noticing good things is like sunshine for your brain. Come back tomorrow and plant some more. 🌞
                    </p>
                </div>
            )}
        </div>
    );
};

// ==========================================
// MAIN COMPONENT
// ==========================================
const KidzCorner = ({ currentUser }) => {
    // --- 1. ACCESS ORG DATA ---
    const { orgData } = useOrganization();
    
    // --- 2. COMPUTE DYNAMIC TITLE ---
    const pageTitle = React.useMemo(() => {
        if (orgData?.navConfig) {
            const configItem = orgData.navConfig.find(i => i.id === 'kidzcorner');
            if (configItem && configItem.label) return configItem.label;
        }
        return "Kidz Corner";
    }, [orgData]);

    // Support outlet for this cohort (memberType 'child') — personal contact
    // and/or a family-eligible EAP. Surfaced ONLY as a discreet parent/guardian
    // footer on the lobby — never a kid-facing button.
    const showSupport = hasSupportOutlet(orgData, currentUser?.memberType);

    const [view, setView] = useState('lobby');
    const [activeGame, setActiveGame] = useState(null);
    const [dynamicTools, setDynamicTools] = useState([]);
    const [activeResource, setActiveResource] = useState(null); 

    const [stars, setStars] = useState(() => {
        return parseInt(localStorage.getItem('kidz_stars') || '0');
    });

    // localStorage stays the offline / signed-out fallback.
    useEffect(() => {
        localStorage.setItem('kidz_stars', stars.toString());
    }, [stars]);

    // RECONCILE STARS WITH THE USER DOC (users/{uid}.kidzStars).
    // Take the higher of local vs remote so neither device nor account loses
    // stars, then write the winner back so both agree. No uid = local-only.
    useEffect(() => {
        if (!currentUser?.uid) return;
        let cancelled = false;
        (async () => {
            try {
                const snap = await getDoc(doc(db, "users", currentUser.uid));
                const remote = snap.exists() ? (parseInt(snap.data().kidzStars, 10) || 0) : 0;
                const local = parseInt(localStorage.getItem('kidz_stars') || '0', 10) || 0;
                const reconciled = Math.max(local, remote);
                if (cancelled) return;
                setStars(reconciled);
                localStorage.setItem('kidz_stars', reconciled.toString());
                if (reconciled !== remote) {
                    await setDoc(doc(db, "users", currentUser.uid), { kidzStars: reconciled }, { merge: true });
                }
            } catch (e) {
                console.error('Error syncing Kidz stars:', e);
            }
        })();
        return () => { cancelled = true; };
    }, [currentUser?.uid]);

    // FETCH DYNAMIC RESOURCES
    useEffect(() => {
        const unsubscribe = onSnapshot(collection(db, "kidz_corner_resources"), (snapshot) => {
            const items = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setDynamicTools(items);
        });
        return () => unsubscribe();
    }, []);

    const addStars = (amount) => {
        setStars(prev => {
            const next = prev + amount;
            // Mirror to the user doc so stars survive refresh / device changes.
            // (localStorage is updated by the effect above.) No uid = local-only.
            if (currentUser?.uid) {
                setDoc(doc(db, "users", currentUser.uid), { kidzStars: next }, { merge: true })
                    .catch((e) => console.error('Error saving Kidz stars:', e));
            }
            return next;
        });
    };

    // --- REPLACED HELPER ---
    // Extract the raw ID instead of formatting the URL
    const getYouTubeVideoId = (url) => {
        if (!url) return null;
        const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
        const match = url.match(regExp);
        return (match && match[2].length === 11) ? match[2] : null;
    };

    const getPdfEmbedUrl = (url) => {
        if (!url) return '';
        if (url.includes('drive.google.com') && url.includes('/view')) {
            return url.replace('/view', '/preview');
        }
        return `https://docs.google.com/viewer?url=${encodeURIComponent(url)}&embedded=true`;
    };

    // Helper for icons
    const getIcon = (type) => {
        if (type === 'Audio') return <Headphones size={24} />;
        if (type === 'Video') return <Play size={24} />;
        if (type === 'PDF') return <Download size={24} />;
        return <FileText size={24} />;
    };

    // Helper for Card Styles
    const getCardStyle = (index) => {
        const styles = [
            { bg: "bg-blue-50", border: "border-blue-200", text: "text-blue-600", icon: "bg-blue-100" },
            { bg: "bg-orange-50", border: "border-orange-200", text: "text-orange-600", icon: "bg-orange-100" },
            { bg: "bg-purple-50", border: "border-purple-200", text: "text-purple-600", icon: "bg-purple-100" },
            { bg: "bg-emerald-50", border: "border-emerald-200", text: "text-emerald-600", icon: "bg-emerald-100" },
            { bg: "bg-pink-50", border: "border-pink-200", text: "text-pink-600", icon: "bg-pink-100" },
        ];
        return styles[index % styles.length];
    };

    // --- VIEW: LOBBY ---
    if (view === 'lobby') {
        return (
            <div className="space-y-8 animate-in fade-in max-w-5xl mx-auto">
                <div className="flex justify-between items-center bg-white p-4 rounded-2xl border border-slate-200 shadow-sm">
                    <h1 className="text-2xl font-black text-slate-800 tracking-tight">{pageTitle}</h1>
                    <div className="flex items-center gap-2 bg-yellow-100 px-4 py-2 rounded-full border border-yellow-200">
                        <Star className="text-yellow-500 fill-yellow-500" size={20} />
                        <span className="font-bold text-yellow-700">{stars} Stars</span>
                    </div>
                </div>
                <div className={`grid grid-cols-1 gap-6 h-[60vh] md:h-[50vh] ${isFinanceHubEnabled(orgData) ? 'md:grid-cols-3' : 'md:grid-cols-2'}`}>
                    <button onClick={() => setView('arcade')} className="relative group overflow-hidden rounded-3xl bg-gradient-to-br from-indigo-500 to-purple-600 text-white shadow-xl flex flex-col justify-center items-center text-center p-8 hover:scale-[1.02] transition-all">
                        <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl translate-x-1/3 -translate-y-1/3 group-hover:bg-white/20 transition-colors"></div>
                        <Gamepad2 size={64} className="mb-4 drop-shadow-lg" />
                        <h2 className="text-3xl font-black mb-2">Game Arcade</h2>
                        <p className="text-indigo-100 font-medium">Play games, earn stars, and have fun!</p>
                        <div className="mt-6 px-6 py-2 bg-white/20 rounded-full font-bold text-sm backdrop-blur-sm group-hover:bg-white/30 transition-colors">Enter Arcade</div>
                    </button>
                    <button onClick={() => setView('library')} className="relative group overflow-hidden rounded-3xl bg-gradient-to-br from-emerald-400 to-teal-600 text-white shadow-xl flex flex-col justify-center items-center text-center p-8 hover:scale-[1.02] transition-all">
                        <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl translate-x-1/3 -translate-y-1/3 group-hover:bg-white/20 transition-colors"></div>
                        <BookOpen size={64} className="mb-4 drop-shadow-lg" />
                        <h2 className="text-3xl font-black mb-2">Treasure Chest</h2>
                        <p className="text-teal-100 font-medium">Cool printables and tools.</p>
                        <div className="mt-6 px-6 py-2 bg-white/20 rounded-full font-bold text-sm backdrop-blur-sm group-hover:bg-white/30 transition-colors">Open Chest</div>
                    </button>
                    {/* MONEY SMARTS — partner-authored (Scotia), kid tier only; org-gated */}
                    {isFinanceHubEnabled(orgData) && (
                        <button onClick={() => setView('money')} className="relative group overflow-hidden rounded-3xl bg-gradient-to-br from-amber-400 to-orange-500 text-white shadow-xl flex flex-col justify-center items-center text-center p-8 hover:scale-[1.02] transition-all">
                            <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl translate-x-1/3 -translate-y-1/3 group-hover:bg-white/20 transition-colors"></div>
                            <PiggyBank size={64} className="mb-4 drop-shadow-lg" />
                            <h2 className="text-3xl font-black mb-2">Money Smarts</h2>
                            <p className="text-amber-100 font-medium">Saving, earning, and spending on stuff worth it.</p>
                            <div className="mt-6 px-6 py-2 bg-white/20 rounded-full font-bold text-sm backdrop-blur-sm group-hover:bg-white/30 transition-colors">Count Me In</div>
                        </button>
                    )}
                </div>

                {/* PARENT / GUARDIAN SUPPORT — discreet, adult-facing; never a kid-facing button */}
                {showSupport && (
                    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-xl bg-rose-50 text-rose-500 flex items-center justify-center shrink-0"><HeartHandshake size={20} /></div>
                            <div>
                                <p className="text-[10px] font-black text-slate-400 uppercase tracking-wider">For parents &amp; guardians</p>
                                <p className="text-sm font-bold text-slate-700">Someone to talk to about your child&rsquo;s wellbeing</p>
                            </div>
                        </div>
                        <SupportOutlet currentUser={currentUser} label="Contact support" className="px-5 py-2.5 bg-rose-50 text-rose-600 hover:bg-rose-100 border border-rose-100 rounded-xl font-bold text-sm flex items-center gap-2 shrink-0 justify-center" />
                    </div>
                )}
            </div>
        );
    }

    // --- VIEW: ARCADE ---
    if (view === 'money') {
        return <FinanceHubView currentUser={currentUser} tier="kid" onBack={() => setView('lobby')} />;
    }

    if (view === 'arcade') {
        return (
            <div className="space-y-6 animate-in fade-in">
                <div className="flex items-center gap-4">
                    <button onClick={() => { setView('lobby'); setActiveGame(null); }} className="p-2 bg-white border border-slate-200 rounded-full hover:bg-slate-50 transition-colors"><ArrowLeft size={20} className="text-slate-600" /></button>
                    <h2 className="text-2xl font-bold text-slate-800">Game Arcade</h2>
                    <div className="ml-auto flex items-center gap-2 bg-yellow-100 px-3 py-1 rounded-full text-xs font-bold text-yellow-700"><Star size={14} className="fill-yellow-700" /> {stars}</div>
                </div>
                {!activeGame ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-4">
                        <button onClick={() => setActiveGame('breathing')} className="bg-white p-6 rounded-3xl border-b-4 border-slate-200 hover:border-blue-400 hover:translate-y-1 transition-all text-left flex items-center gap-4 group h-32">
                            <div className="w-16 h-16 rounded-2xl bg-blue-100 text-blue-600 flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform"><Sun size={32} /></div>
                            <div><h3 className="font-black text-xl text-slate-800">Breathing Bubble</h3><p className="text-sm text-slate-500 font-medium">Relax & Chill</p></div>
                        </button>
                        <button onClick={() => setActiveGame('memory')} className="bg-white p-6 rounded-3xl border-b-4 border-slate-200 hover:border-purple-400 hover:translate-y-1 transition-all text-left flex items-center gap-4 group h-32">
                            <div className="w-16 h-16 rounded-2xl bg-purple-100 text-purple-600 flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform"><Gamepad2 size={32} /></div>
                            <div><h3 className="font-black text-xl text-slate-800">Emotion Match</h3><p className="text-sm text-slate-500 font-medium">Memory Game</p></div>
                        </button>
                        <button onClick={() => setActiveGame('balloons')} className="bg-white p-6 rounded-3xl border-b-4 border-slate-200 hover:border-pink-400 hover:translate-y-1 transition-all text-left flex items-center gap-4 group h-32">
                            <div className="w-16 h-16 rounded-2xl bg-pink-100 text-pink-600 flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform"><Star size={32} /></div>
                            <div><h3 className="font-black text-xl text-slate-800">Affirmation Pop</h3><p className="text-sm text-slate-500 font-medium">Pop for Positivity!</p></div>
                        </button>
                        <button onClick={() => setActiveGame('simon')} className="bg-white p-6 rounded-3xl border-b-4 border-slate-200 hover:border-yellow-400 hover:translate-y-1 transition-all text-left flex items-center gap-4 group h-32">
                            <div className="w-16 h-16 rounded-2xl bg-yellow-100 text-yellow-600 flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform"><Zap size={32} /></div>
                            <div><h3 className="font-black text-xl text-slate-800">Focus Pattern</h3><p className="text-sm text-slate-500 font-medium">Test your focus!</p></div>
                        </button>
                        <button onClick={() => setActiveGame('feelings')} className="bg-white p-6 rounded-3xl border-b-4 border-slate-200 hover:border-indigo-400 hover:translate-y-1 transition-all text-left flex items-center gap-4 group h-32">
                            <div className="w-16 h-16 rounded-2xl bg-indigo-100 text-indigo-600 flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform"><Smile size={32} /></div>
                            <div><h3 className="font-black text-xl text-slate-800">Feelings Detective</h3><p className="text-sm text-slate-500 font-medium">Match the face to the feeling!</p></div>
                        </button>
                        <button onClick={() => setActiveGame('grounding')} className="bg-white p-6 rounded-3xl border-b-4 border-slate-200 hover:border-teal-400 hover:translate-y-1 transition-all text-left flex items-center gap-4 group h-32">
                            <div className="w-16 h-16 rounded-2xl bg-teal-100 text-teal-600 flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform"><Ear size={32} /></div>
                            <div><h3 className="font-black text-xl text-slate-800">5-4-3-2-1 Calm</h3><p className="text-sm text-slate-500 font-medium">A senses journey back to calm</p></div>
                        </button>
                        <button onClick={() => setActiveGame('garden')} className="bg-white p-6 rounded-3xl border-b-4 border-slate-200 hover:border-emerald-400 hover:translate-y-1 transition-all text-left flex items-center gap-4 group h-32">
                            <div className="w-16 h-16 rounded-2xl bg-emerald-100 text-emerald-600 flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform"><Flower2 size={32} /></div>
                            <div><h3 className="font-black text-xl text-slate-800">Gratitude Garden</h3><p className="text-sm text-slate-500 font-medium">Plant today's good things</p></div>
                        </button>
                    </div>
                ) : (
                    <div className="bg-white p-8 rounded-3xl border border-slate-200 shadow-lg min-h-[500px] flex items-center justify-center relative">
                        <div className="w-full">
                            {activeGame === 'breathing' && <BreathingGame onComplete={addStars} />}
                            {activeGame === 'memory' && <MemoryGame onComplete={addStars} />}
                            {activeGame === 'balloons' && <BalloonGame onComplete={addStars} />}
                            {activeGame === 'simon' && <SimonGame onComplete={addStars} />}
                            {activeGame === 'feelings' && <FeelingsDetectiveGame onComplete={addStars} />}
                            {activeGame === 'grounding' && <GroundingGame onComplete={addStars} />}
                            {activeGame === 'garden' && <GratitudeGarden onComplete={addStars} currentUser={currentUser} />}
                        </div>
                    </div>
                )}
            </div>
       );
    }

    // --- VIEW: LIBRARY ---
    if (view === 'library') {
        return (
            <div className="space-y-6 animate-in fade-in pb-20">
                {/* MODAL VIEWER */}
                {activeResource && (
                    <div className="fixed inset-0 z-[200] bg-slate-900/90 backdrop-blur-sm flex items-center justify-center p-2 md:p-4">
                        <div className="bg-white w-full max-w-4xl rounded-2xl overflow-hidden shadow-2xl flex flex-col h-[90vh] md:h-[85vh]">
                            <div className="p-4 border-b border-slate-100 flex justify-between items-center bg-slate-50 sticky top-0 z-10 shrink-0">
                                <h3 className="font-bold text-sm md:text-lg text-slate-800 flex items-center gap-3 truncate pr-2">
                                    <div className="p-2 bg-blue-100 text-blue-600 rounded-lg shrink-0">{getIcon(activeResource.type)}</div>
                                    <span className="truncate">{activeResource.title}</span>
                                </h3>
                                <button onClick={() => setActiveResource(null)} className="p-2 hover:bg-slate-200 rounded-full transition-colors shrink-0"><X size={20} className="text-slate-500"/></button>
                            </div>
                            <div className="flex-1 bg-slate-100 relative overflow-hidden flex flex-col">
                                
                                {/* THE BULLETPROOF VIDEO PLAYER */}
                                {activeResource.type === 'Video' && (
                                    <div className="w-full h-full bg-black flex items-center justify-center p-2 md:p-6 overflow-hidden">
                                        <div 
                                            className="w-full relative" 
                                            style={{ aspectRatio: '16/9', maxHeight: '100%', maxWidth: '1024px' }}
                                        >
                                            <CustomYouTubePlayer 
                                                videoId={getYouTubeVideoId(activeResource.url)} 
                                            />
                                        </div>
                                    </div>
                                )}

                                {(activeResource.type === 'PDF' || activeResource.type === 'Link') && (
                                    <div className="w-full h-full relative group">
                                        <iframe src={getPdfEmbedUrl(activeResource.url)} className="w-full h-full" frameBorder="0" title="Resource Viewer"></iframe>
                                        <div className="absolute bottom-6 right-6 opacity-0 group-hover:opacity-100 transition-opacity">
                                            <a href={activeResource.url} target="_blank" rel="noopener noreferrer" className="px-4 py-2 bg-slate-900 text-white text-xs font-bold rounded-lg shadow-xl flex items-center gap-2 hover:bg-black transition-colors"><ExternalLink size={14}/> Open in New Tab</a>
                                        </div>
                                    </div>
                                )}
                                {activeResource.type === 'Template' && (
                                    <div className="overflow-y-auto h-full p-4 md:p-8">
                                        <pre className="whitespace-pre-wrap font-sans text-slate-600 text-sm md:text-base leading-relaxed p-4 md:p-8 bg-white rounded-xl border border-slate-200 shadow-sm max-w-3xl mx-auto">{activeResource.content || "No content available."}</pre>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                <div className="flex items-center gap-4">
                    <button onClick={() => setView('lobby')} className="p-2 bg-white border border-slate-200 rounded-full hover:bg-slate-50 transition-colors"><ArrowLeft size={20} className="text-slate-600" /></button>
                    <h2 className="text-2xl font-black text-slate-800">Treasure Chest</h2>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {dynamicTools.map((tool, idx) => {
                        const style = getCardStyle(idx);
                        return (
                            <div 
                                key={tool.id || idx} 
                                onClick={() => setActiveResource(tool)} 
                                className={`group relative bg-white border-b-4 border-r-4 ${style.border} rounded-3xl p-6 hover:-translate-y-1 hover:shadow-lg transition-all flex flex-col items-start gap-4 cursor-pointer`}
                            >
                                <div className="flex justify-between items-start w-full">
                                    <div className={`w-14 h-14 rounded-2xl ${style.icon} ${style.text} flex items-center justify-center shrink-0`}>
                                        {getIcon(tool.type)}
                                    </div>
                                    <span className="bg-slate-100 text-slate-500 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-lg">
                                        {tool.type}
                                    </span>
                                </div>
                                <div>
                                    <h3 className="font-black text-lg text-slate-800 leading-tight mb-2">{tool.title}</h3>
                                    <div className="flex flex-wrap gap-2">
                                        {tool.tags && tool.tags.map((tag, i) => (
                                            <span key={i} className={`text-[10px] font-bold ${style.bg} ${style.text} px-2 py-1 rounded-md`}>
                                                #{tag}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                                <div className={`mt-auto w-full py-2 ${style.bg} ${style.text} rounded-xl font-bold text-sm text-center opacity-0 group-hover:opacity-100 transition-opacity`}>
                                    Open Resource
                                </div>
                            </div>
                        );
                    })}
                    {dynamicTools.length === 0 && <div className="col-span-full py-12 text-center text-slate-400">No treasures found yet!</div>}
                </div>
            </div>
        );
    }
};

export default KidzCorner;